import { httpClient } from './http-client';
import {
  Household,
  HouseholdWithRole,
  HouseholdMember,
  HouseholdMemberWithUser,
  CreateHouseholdRequest,
  UpdateHouseholdRequest,
  AddHouseholdMemberRequest,
  UpdateMemberRoleRequest,
  ApiResponse,
  UserRole,
} from '../types/api';

export class HouseholdsService {
  private static readonly ENDPOINTS = {
    HOUSEHOLDS: '/households',
    HOUSEHOLD_BY_ID: (id: string) => `/households/${id}`,
    HOUSEHOLD_MEMBERS: (id: string) => `/households/${id}/members`,
    HOUSEHOLD_MEMBER: (householdId: string, userId: string) => `/households/${householdId}/members/${userId}`,
    USER_HOUSEHOLDS: '/households/user',
    /** The caller's household, resolved from the token rather than a path param. */
    CURRENT_HOUSEHOLD: '/households/current',
    HOUSEHOLD_SETTINGS: '/households/settings',
  } as const;

  /**
   * Create a new household
   */
  async createHousehold(householdData: CreateHouseholdRequest): Promise<ApiResponse<Household>> {
    return httpClient.post(HouseholdsService.ENDPOINTS.HOUSEHOLDS, householdData);
  }

  /**
   * Get all households for the current user
   */
  async getUserHouseholds(): Promise<ApiResponse<HouseholdWithRole[]>> {
    return httpClient.get(HouseholdsService.ENDPOINTS.USER_HOUSEHOLDS);
  }

  /**
   * Get a specific household by ID
   */
  async getHousehold(id: string): Promise<ApiResponse<Household>> {
    return httpClient.get(HouseholdsService.ENDPOINTS.HOUSEHOLD_BY_ID(id));
  }

  /**
   * Update an existing household
   */
  async updateHousehold(_id: string, updates: UpdateHouseholdRequest): Promise<ApiResponse<Household>> {
    // The household comes from the caller's token, not the path, so the id is
    // accepted for call-site readability but not sent.
    return httpClient.put(HouseholdsService.ENDPOINTS.HOUSEHOLD_SETTINGS, updates);
  }

  /**
   * Delete a household
   */
  async deleteHousehold(id: string): Promise<ApiResponse<void>> {
    return httpClient.delete(HouseholdsService.ENDPOINTS.HOUSEHOLD_BY_ID(id));
  }

  /**
   * Get all members of a household
   */
  async getHouseholdMembers(householdId: string): Promise<ApiResponse<HouseholdMemberWithUser[]>> {
    return httpClient.get(HouseholdsService.ENDPOINTS.HOUSEHOLD_MEMBERS(householdId));
  }

  /**
   * Add a member to a household
   */
  async addHouseholdMember(
    householdId: string,
    memberData: AddHouseholdMemberRequest
  ): Promise<ApiResponse<HouseholdMember>> {
    return httpClient.post(HouseholdsService.ENDPOINTS.HOUSEHOLD_MEMBERS(householdId), memberData);
  }

  /**
   * Get a specific household member
   */
  async getHouseholdMember(
    householdId: string,
    userId: string
  ): Promise<ApiResponse<HouseholdMemberWithUser>> {
    return httpClient.get(HouseholdsService.ENDPOINTS.HOUSEHOLD_MEMBER(householdId, userId));
  }

  /**
   * Update a household member's role
   */
  async updateMemberRole(
    householdId: string,
    userId: string,
    roleData: UpdateMemberRoleRequest
  ): Promise<ApiResponse<HouseholdMember>> {
    return httpClient.put(HouseholdsService.ENDPOINTS.HOUSEHOLD_MEMBER(householdId, userId), roleData);
  }

  /**
   * Remove a member from a household
   */
  async removeHouseholdMember(householdId: string, userId: string): Promise<ApiResponse<void>> {
    return httpClient.delete(HouseholdsService.ENDPOINTS.HOUSEHOLD_MEMBER(householdId, userId));
  }

  /**
   * Check if user is a member of a household
   */
  async checkMembership(householdId: string, userId: string): Promise<boolean> {
    try {
      await this.getHouseholdMember(householdId, userId);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Get user's role in a household
   */
  async getUserRole(householdId: string): Promise<UserRole | null> {
    try {
      const households = await this.getUserHouseholds();
      const household = households.data.find(h => h.id === householdId);
      return household?.role || null;
    } catch {
      return null;
    }
  }

  /**
   * Get all members of the current household
   */
  async getMembers(): Promise<ApiResponse<any[]>> {
    try {
      const rawResponse = await httpClient.get<any>('/households/members');
      return {
        data: rawResponse.data || rawResponse,
        message: rawResponse.message,
        error: rawResponse.error,
      };
    } catch (error: any) {
      return {
        data: [],
        error: error.message || 'Failed to get household members',
      };
    }
  }

  /**
   * Invite a user to a household by email
   */
  async inviteUserByEmail(email: string, role: UserRole = 'Member'): Promise<ApiResponse<any>> {
    try {
      const rawResponse = await httpClient.post<any>('/households/invite', {
        email,
        role,
      });
      return {
        data: rawResponse.data || rawResponse,
        message: rawResponse.message,
        error: rawResponse.error,
      };
    } catch (error: any) {
      return {
        data: null,
        error: error.message || 'Failed to send invitation',
      };
    }
  }

  /**
   * Accept a household invitation by household ID
   */
  async acceptInvitation(householdId: string): Promise<ApiResponse<any>> {
    try {
      const rawResponse = await httpClient.post<any>(`/households/${householdId}/accept`, {});
      return {
        data: rawResponse.data || rawResponse,
        message: rawResponse.message,
        error: rawResponse.error,
      };
    } catch (error: any) {
      return {
        data: null,
        error: error.message || 'Failed to accept invitation',
      };
    }
  }

  /**
   * Reject/decline a household invitation by household ID
   */
  async rejectInvitation(householdId: string): Promise<ApiResponse<void>> {
    try {
      const rawResponse = await httpClient.post<any>(`/households/${householdId}/reject`, {});
      return {
        data: undefined,
        message: rawResponse.message || 'Invitation rejected',
        error: rawResponse.error,
      };
    } catch (error: any) {
      return {
        data: undefined,
        error: error.message || 'Failed to reject invitation',
      };
    }
  }

  /**
   * Get pending invitations for the current household
   */
  async getPendingInvitations(): Promise<ApiResponse<any[]>> {
    try {
      const rawResponse = await httpClient.get<any>('/households/invitations/pending');
      return {
        data: rawResponse.data || rawResponse || [],
        message: rawResponse.message,
        error: rawResponse.error,
      };
    } catch (error: any) {
      return {
        data: [],
        error: error.message || 'Failed to get pending invitations',
      };
    }
  }

  /**
   * Get my pending invitations (invitations sent to me)
   */
  async getMyPendingInvitations(): Promise<ApiResponse<any[]>> {
    try {
      const rawResponse = await httpClient.get<any>('/households/invitations/my-pending');
      return {
        data: rawResponse.data || rawResponse || [],
        message: rawResponse.message,
        error: rawResponse.error,
      };
    } catch (error: any) {
      return {
        data: [],
        error: error.message || 'Failed to get my pending invitations',
      };
    }
  }

  /**
   * Remove a member from the current household
   */
  async removeMember(userId: string): Promise<ApiResponse<void>> {
    try {
      const rawResponse = await httpClient.delete<any>(`/households/members/${userId}`);
      return {
        data: undefined,
        message: rawResponse.message || 'Member removed',
        error: rawResponse.error,
      };
    } catch (error: any) {
      return {
        data: undefined,
        error: error.message || 'Failed to remove member',
      };
    }
  }

  /**
   * Leave a household (remove self as member)
   */
  async leaveHousehold(householdId: string): Promise<ApiResponse<void>> {
    return httpClient.post(`${HouseholdsService.ENDPOINTS.HOUSEHOLD_BY_ID(householdId)}/leave`);
  }

  /**
   * Transfer ownership of a household
   */
  async transferOwnership(
    householdId: string,
    newOwnerId: string
  ): Promise<ApiResponse<Household>> {
    return httpClient.post(`${HouseholdsService.ENDPOINTS.HOUSEHOLD_BY_ID(householdId)}/transfer-ownership`, {
      new_owner_id: newOwnerId,
    });
  }

  /**
   * Get household statistics
   */
  async getHouseholdStats(householdId: string): Promise<{
    totalMembers: number;
    membersByRole: Record<UserRole, number>;
    totalAccounts: number;
    totalSnapshots: number;
    baseCurrency: string;
    createdAt: string;
  }> {
    // This would typically be a single API call, but we'll composite multiple calls
    const [household, members] = await Promise.all([
      this.getHousehold(householdId),
      this.getHouseholdMembers(householdId),
    ]);

    const membersByRole: Record<UserRole, number> = {
      Owner: 0,
      Member: 0,
      Viewer: 0,
    };

    members.data.forEach(member => {
      membersByRole[member.role] = (membersByRole[member.role] || 0) + 1;
    });

    return {
      totalMembers: members.data.length,
      membersByRole,
      totalAccounts: 0, // Would need to fetch from accounts service
      totalSnapshots: 0, // Would need to fetch from snapshots service
      baseCurrency: household.data.base_currency,
      createdAt: household.data.created_at,
    };
  }

  /**
   * Search households by name
   */
  async searchHouseholds(query: string): Promise<ApiResponse<HouseholdWithRole[]>> {
    const response = await this.getUserHouseholds();
    const filteredHouseholds = response.data.filter(household =>
      household.name.toLowerCase().includes(query.toLowerCase())
    );

    return {
      data: filteredHouseholds,
      message: response.message,
      error: response.error,
    };
  }

  /**
   * Get households where user is owner
   */
  async getOwnedHouseholds(): Promise<ApiResponse<HouseholdWithRole[]>> {
    const response = await this.getUserHouseholds();
    const ownedHouseholds = response.data.filter(household => household.role === 'Owner');

    return {
      data: ownedHouseholds,
      message: response.message,
      error: response.error,
    };
  }

  /**
   * Fetch the caller's household from the server, base currency included.
   *
   * Distinct from getCurrentHousehold(), which reads a cached copy from
   * localStorage and can be stale.
   */
  async fetchCurrentHousehold(): Promise<ApiResponse<Household>> {
    return httpClient.get(HouseholdsService.ENDPOINTS.CURRENT_HOUSEHOLD);
  }

  /**
   * Get the current active household from localStorage
   */
  getCurrentHousehold(): Household | null {
    try {
      const stored = localStorage.getItem('folioworth_household');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }

  /**
   * Set the current active household in localStorage
   */
  setCurrentHousehold(household: Household): void {
    localStorage.setItem('folioworth_household', JSON.stringify(household));
  }

  /**
   * Clear the current household from localStorage
   */
  clearCurrentHousehold(): void {
    localStorage.removeItem('folioworth_household');
  }
}

// Export singleton instance
export const householdsService = new HouseholdsService();
